/**
 * InsForge-native staff password reset (code method).
 *
 * The operator requests a reset code by email, then exchanges the emailed code
 * for a one-time token and sets a new password. InsForge owns the credential end
 * to end — this matches the project's `resetPasswordMethod: "code"` auth config.
 * Used both for a forgotten password and for an invited operator setting their
 * first password (the `admin.invite` edge action sends the same reset email).
 */

import { insforge } from "@/lib/insforge";

const REQUEST_FAILED = "We couldn't send a reset code. Check the email and try again.";
const RESET_FAILED = "We couldn't reset your password. Check the code and try again.";

/** Step 1 — email the operator a reset code. */
export async function sendStaffPasswordReset(email: string): Promise<void> {
  const { data, error } = await insforge.auth.sendResetPasswordEmail({ email });
  if (error || !data?.success) {
    throw new Error(error?.message || REQUEST_FAILED);
  }
}

/** Exchange the emailed reset code for the one-time token used by resetPassword. */
async function exchangeResetCodeForToken(email: string, code: string): Promise<string> {
  const exchange = await insforge.auth.exchangeResetPasswordToken({
    code,
    email,
  });
  // The response type is owned by @insforge/shared-schemas; read the one-time
  // token defensively so the flow is resilient to that package's field naming.
  const token = (exchange.data as { readonly token?: string } | null)?.token ?? "";
  if (exchange.error || !token) {
    throw new Error(exchange.error?.message || RESET_FAILED);
  }
  return token;
}

/** Step 2 — exchange the emailed code for a token, then set the new password. */
export async function completeStaffPasswordReset(input: {
  readonly code: string;
  readonly email: string;
  readonly newPassword: string;
}): Promise<void> {
  const token = await exchangeResetCodeForToken(input.email, input.code);
  const { error } = await insforge.auth.resetPassword({
    newPassword: input.newPassword,
    otp: token,
  });
  if (error) {
    throw new Error(error.message || RESET_FAILED);
  }
}
